import { z } from "zod";

import { useWizard } from "@performyard/py-components";

export const COMPENSATION_CYCLE_NAME_MAX_LENGTH = 255;

export const requiredDate = z
	.date()
	.nullable()
	.refine((val): val is Date => val !== null, { message: "This field is required" });

export const cycleDetailsSchema = z
	.object({
		name: z
			.string()
			.min(1, "Cycle name is required")
			.max(
				COMPENSATION_CYCLE_NAME_MAX_LENGTH,
				`Cycle name must be ${COMPENSATION_CYCLE_NAME_MAX_LENGTH} characters or fewer`
			),
		startDate: requiredDate,
		dueDate: requiredDate,
	})
	.refine((data) => !data.startDate || !data.dueDate || data.dueDate > data.startDate, {
		message: "Due date must be after start date",
		path: ["dueDate"],
	});

export const allotmentBudgetSchema = z
	.object({
		allotmentIncreaseFixed: z.number().min(0).nullish(),
		totalEligibleCompensation: z.number().min(0).default(0),
	})
	.superRefine((data, ctx) => {
		const fixed = data.allotmentIncreaseFixed;
		if (fixed === undefined || fixed === null) {
			ctx.addIssue({
				code: "custom",
				message: "Enter a percent increase and a fixed increase",
				path: ["allotmentIncreaseFixed"],
			});
			return;
		}
		if (fixed <= 0) {
			ctx.addIssue({
				code: "custom",
				message: "Increase must be greater than 0",
				path: ["allotmentIncreaseFixed"],
			});
		}
	});

export const approvalChainSchema = z
	.object({
		approvalType: z.enum(["manager", "chain"]),
		managerOverrides: z
			.record(
				z.string(),
				z.object({
					label: z.string(),
					value: z.string(),
				})
			)
			.default({})
			.nullable(),
	})
	.refine((data) => data.approvalType === "manager" || data.approvalType === "chain", {
		message: "Approval type must be either manager or chain",
		path: ["approvalType"],
	});

export type CycleDetailsData = z.infer<typeof cycleDetailsSchema>;
export type AllotmentBudgetData = z.infer<typeof allotmentBudgetSchema>;
export type ApprovalChainData = z.infer<typeof approvalChainSchema>;

export type CompensationWizardData = {
	"cycle-details"?: CycleDetailsData;
	"cycle-budget"?: AllotmentBudgetData;
	"approval-chain"?: ApprovalChainData;
};

export const useCompensationWizardData = (): CompensationWizardData => {
	const { wizardData } = useWizard();
	return wizardData as CompensationWizardData;
};
